import { useGetConceptGroupsQuery } from '@verantos-dev/data-access-concept-groups';
import {
  AnalyticalVariableListItem,
  AvDenovoPreviewModal,
  AVPreviewModal,
  toConceptGroupOptions,
} from '@verantos-dev/feature-analytical-variables';
import { formatDate } from '@verantos-dev/shared-utils';

interface AvLibraryDetailModalProps {
  analyticalVariable: AnalyticalVariableListItem;
  onClose: () => void;
}

/**
 * Detail modal for the AV library list page.
 *
 * Shape-aware:
 * - `kind === 'av_ir'`: structured AV. Fetch concept groups and render the
 *   read-only `AVPreviewModal` (logic tree, format, window).
 * - `kind === 'av_denovo'`: SQL-first AV (de novo / agent path). Delegates to
 *   ``AvDenovoPreviewModal``, which fetches the full ``VariableDetail`` and
 *   renders the complete ``AvContentCard`` tabset (Description / SQL /
 *   Concepts / Decisions / Verification — auto-filtered by content) so the
 *   library surface shows the same rich content the chat drawer renders
 *   from the live stream. No ``viewFullHref`` is passed — the library list
 *   page is already the destination, not a stepping stone.
 *
 * The "Edit variable" button is intentionally omitted (library view only).
 */
export const AvLibraryDetailModal = ({
  analyticalVariable,
  onClose,
}: AvLibraryDetailModalProps) => {
  const { data: conceptGroupsResponse, isLoading: isConceptGroupsLoading } =
    useGetConceptGroupsQuery({ urlParams: '' });

  const lastModified = analyticalVariable?.createdBy?.transactionDate
    ? formatDate(analyticalVariable?.createdBy?.transactionDate, 'dateTimeFull')
    : undefined;

  if (analyticalVariable.kind === 'av_denovo') {
    return (
      <AvDenovoPreviewModal
        id="av-library-detail-modal"
        analyticalVariableId={analyticalVariable.analyticalVariableId}
        isOpen
        onClose={onClose}
      />
    );
  }

  // `kind === 'av_ir'` — structured AV. The non-null assertion is safe per
  // the wire contract: av_ir AVs always carry `jsonValue`.
  return (
    <AVPreviewModal
      id="av-library-detail-modal"
      analyticalVariable={analyticalVariable.jsonValue!}
      conceptGroups={toConceptGroupOptions(conceptGroupsResponse?.data)}
      onClose={onClose}
      owner={analyticalVariable?.owner}
      lastModified={lastModified}
      loading={isConceptGroupsLoading}
    />
  );
};
